export function nextPowerOf2 (n) {
  if (n <= 0) return 1;
  let p = 1;
  while (p < n) p <<= 1;
  return p;
}

// Number of knockout rounds for a bracket given its configured size and how many squads
// actually participate. Fewer than two teams means no bracket.
export function bracketRoundCount (bracketSize, squadCount) {
  const teams = Math.min(bracketSize || 0, squadCount || 0);
  if (teams < 2) return 0;
  return Math.log2(nextPowerOf2(teams));
}

// Display name for a bracket round. Rounds are counted from the final backwards:
// round === numRounds is the Final, numRounds - 1 the Semi-final, and so on. Rounds before
// the quarter-final are "Round of 16", "Round of 32", etc.
export function bracketRoundName (numRounds, round) {
  if (numRounds < 1 || round > numRounds || round < 1) return `Round ${round}`;
  const offset = numRounds - round;
  if (offset === 0) return "Final";
  if (offset === 1) return "Semi-final";
  if (offset === 2) return "Quarter-final";
  return `Round of ${2 ** (offset + 1)}`;
}

// Display name for a persisted bracket slot like "R1.2", "FINAL" or "THIRD". Rounds map to their
// named label; the final reads as "Final" and the third-place consolation as "3rd Place".
export function bracketSlotName (bracketSlot, numRounds) {
  if (bracketSlot === "THIRD") return "3rd Place";
  const match = /^R(\d+)\./.exec(bracketSlot || "");
  if (match) {
    const round = Number(match[1]);
    if (numRounds >= 2) return bracketRoundName(numRounds, round);
  }
  return "Final";
}

// True when the bracket should include a third-place consolation match: more than three
// teams actually participate (a three-team bracket has a semifinal bye and no two true
// semifinal losers to pair up).
export function hasConsolation (teamCount) {
  return (teamCount || 0) > 3;
}

// Standard tennis-style seeding positions for a full bracket, placing the top seeds in
// opposite halves so seed 1 and seed 2 can only meet in the final. Each seed in the
// previous half gets its complement (n + 1 - seed) placed next to it.
// For fullSize=8: positions = [1, 8, 4, 5, 2, 7, 3, 6]
export function seededPositions (n) {
  if (n === 2) return [1, 2];
  const half = seededPositions(n / 2);
  return half.flatMap(seed => [seed, n + 1 - seed]);
}

// Build a single-elimination bracket tree for `b` teams using standard seeding.
// Returns events with: round, slot, homeSeed, awaySeed, bracketSlot.
// Teams with no round-1 opponent get a bye (awaySeed is null).
export function buildBracketTree (b) {
  if (b < 2) return [];
  const fullSize = nextPowerOf2(b);
  const rounds = Math.log2(fullSize);
  const positions = seededPositions(fullSize);
  const seeds = positions.map(s => (s <= b ? s : null));

  const events = [];
  function fillRound (round, slotSeeds) {
    const next = [];
    for (let i = 0; i < slotSeeds.length; i += 2) {
      const a = slotSeeds[i];
      const aNext = slotSeeds[i + 1];
      const slot = events.length + 1;
      const label = round === rounds ? "FINAL" : `R${round}.${slot}`;
      events.push({
        round,
        slot,
        homeSeed: a,
        awaySeed: aNext,
        bracketSlot: label
      });
      next.push(a !== null ? a : null);
    }
    return next;
  }

  let currentSeeds = seeds;
  for (let r = 1; r <= rounds; r++) {
    currentSeeds = fillRound(r, currentSeeds);
  }
  return events;
}

// Resolves which squads are in every bracket slot based on actual race outcomes. Round 1 is
// seeded from `seeds` (ranked squads, best first); a structural bye (awaySeed null) auto-advances
// its single squad. Later rounds take the winners of their feeder slots; a feeder that has not
// been decided leaves that side `undefined`. `winnerOf(slotId, homeSquadId, awaySquadId)` returns
// the winning squad id for a two-squad slot, or undefined when its race is not decided yet.
// When the league has a third-place consolation, the two semifinal losers are paired into a
// `THIRD` slot; once the first semifinal loser is known the slot holds that squad with the
// other side still `undefined`, so displays can name it before the second semifinal resolves.
// Returns a map of bracketSlot -> [homeSquadId|undefined, awaySquadId|undefined].
export function resolveBracketSquads ({ bracketSize, seeds, winnerOf }) {
  const result = {};
  if (bracketSize < 2) return result;
  const fullSize = nextPowerOf2(bracketSize);
  const rounds = Math.log2(fullSize);
  const leaves = seededPositions(fullSize).map(seed => (seed <= seeds.length ? seeds[seed - 1] : undefined));

  let prevRaces = [];
  let semifinalLosers = [];
  let globalSlot = 0;
  for (let round = 1; round <= rounds; round++) {
    const races = [];
    const count = fullSize / Math.pow(2, round);
    for (let p = 0; p < count; p++) {
      globalSlot++;
      const slotId = round === rounds ? "FINAL" : `R${round}.${globalSlot}`;
      let home;
      let away;
      if (round === 1) {
        home = leaves[2 * p];
        away = leaves[2 * p + 1];
      } else {
        home = prevRaces[2 * p] ? prevRaces[2 * p].winner : undefined;
        away = prevRaces[2 * p + 1] ? prevRaces[2 * p + 1].winner : undefined;
      }
      const winner = away === undefined
        ? home
        : (home !== undefined && winnerOf ? winnerOf(slotId, home, away) : undefined);
      races.push({ slotId, home, away, winner });
      result[slotId] = [home, away];

      // A two-squad decided slot contributes its loser; the semifinal round's losers become
      // the third-place pairing.
      if (round === rounds - 1 && away !== undefined && winner !== undefined) {
        semifinalLosers.push(winner === home ? away : home);
      }
    }
    prevRaces = races;
  }

  if (semifinalLosers.length === 2) {
    result.THIRD = [semifinalLosers[0], semifinalLosers[1]];
  } else if (semifinalLosers.length === 1 && hasConsolation(seeds.length)) {
    result.THIRD = [semifinalLosers[0], undefined];
  }

  return result;
}

function toDate (value) {
  if (value && typeof value.toDate === "function") return value.toDate();
  return new Date(value);
}

function dateKey (date) {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

function sundayBefore (date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - d.getDay());
  return d;
}

// The date for the given slot in calendar week n (1-indexed), anchored at
// sundayAlignedStart. Returns null if the date is blacked out.
function nthOccurrence ({ sundayAlignedStart, blackoutKeys, slot, n }) {
  const date = new Date(sundayAlignedStart);
  date.setDate(date.getDate() + (n - 1) * 7 + slot.dayOfWeek);
  if (blackoutKeys.has(dateKey(date))) return null;
  date.setHours(Math.floor(slot.time / 60), slot.time % 60, 0, 0);
  return date;
}

// Schedules the single-elimination bracket games once seeds are known. Higher seeds host. A
// game that cannot fit at its home venue in a week is first relocated to another venue in that
// same week; only if no venue can host it that week does it defer to a later week (internal
// fallback — never surfaced). Later rounds cascade behind any deferred games. Returns a map of
// bracketSlot -> { locationId, date, tableNumber, week, bumped, deferred }:
//   - bumped   true when the game lands at a venue other than its higher seed's home
//   - deferred true when the game lands in a week after its nominal one
//   - unplaceable games (no venue anywhere) => locationId/date/tableNumber/week all null.
// `bracketSlots` maps bracketSlot -> { week, round, squadIds }, squadIds with higher seed first.
export function assignBracketSchedule ({ bracketSlots, squads, locations, startDate, blackoutDates }) {
  const result = {};
  if (!bracketSlots || Object.keys(bracketSlots).length === 0) return result;

  const squadById = new Map(squads.map(s => [s.id, s]));
  const locationById = new Map(locations.map(l => [l.id, l]));
  const blackoutKeys = new Set((blackoutDates || []).map(d => (typeof d === "string" ? d : dateKey(toDate(d)))));
  const sundayAlignedStart = sundayBefore(toDate(startDate));

  // Normalize each slot, keeping byes out of the placement pass.
  const byRound = new Map();
  for (const [slotId, slot] of Object.entries(bracketSlots)) {
    const ids = slot.squadIds || [];
    if (ids.length < 2) {
      result[slotId] = { locationId: null, date: null, tableNumber: null, week: null, bumped: false, deferred: false };
      continue;
    }
    const home = squadById.get(ids[0]);
    if (!home) {
      result[slotId] = { locationId: null, date: null, tableNumber: null, week: null, bumped: true, deferred: false };
      continue;
    }
    const round = slot.round || 1;
    if (!byRound.has(round)) byRound.set(round, { nominalWeek: Infinity, matches: [] });
    const group = byRound.get(round);
    if (slot.week != null) group.nominalWeek = Math.min(group.nominalWeek, slot.week);
    group.matches.push({
      slotId,
      nominalWeek: slot.week,
      home,
      away: squadById.get(ids[1]),
      seed: slot.homeSeed || Infinity
    });
  }
  const rounds = [...byRound.keys()].sort((a, b) => a - b);
  for (const round of rounds) {
    byRound.get(round).matches.sort((a, b) => a.seed - b.seed);
  }

  const tableLoad = new Map();
  function load (week, locId, slotIndex) {
    return tableLoad.get(`${week}|${locId}|${slotIndex}`) || 0;
  }
  function setLoad (week, locId, slotIndex, value) {
    tableLoad.set(`${week}|${locId}|${slotIndex}`, value);
  }

  // Places a match at the first venue slot-time with a free table in the given week. Returns
  // { locationId, date, tableNumber } or null when the venue is at capacity that week.
  function placeAt (week, loc) {
    if (!loc) return null;
    const slots = loc.slots || [];
    for (let index = 0; index < slots.length; index++) {
      const slot = slots[index];
      if (load(week, loc.id, index) >= loc.numberOfTables) continue;
      const date = nthOccurrence({ sundayAlignedStart, blackoutKeys, slot, n: week });
      if (!date) continue;
      const tableNumber = load(week, loc.id, index) + 1;
      setLoad(week, loc.id, index, tableNumber);
      return { locationId: loc.id, date, tableNumber };
    }
    return null;
  }

  // Relocate order for a match at a given week: home venue first, then away, then the rest.
  function relocationOrder (match, week) {
    const homeLoc = locationById.get(match.home.locationId);
    const awayLoc = match.away && locationById.get(match.away.locationId);
    const order = [];
    if (homeLoc) order.push(homeLoc);
    if (awayLoc && awayLoc.id !== homeLoc?.id) order.push(awayLoc);
    for (const loc of locations) {
      if (loc.id !== homeLoc?.id && loc.id !== awayLoc?.id) order.push(loc);
    }
    return order;
  }

  // Try to place a match at a specific week: home first, then relocation within the same week.
  // Returns { placement, bumped } where placement is null if nothing fits that week.
  function placeAtWeek (match, week) {
    const homeLoc = locationById.get(match.home.locationId);
    const atHome = placeAt(week, homeLoc);
    if (atHome) return { placement: atHome, bumped: false };
    for (const loc of relocationOrder(match, week)) {
      if (loc.id === homeLoc?.id) continue;
      const placed = placeAt(week, loc);
      if (placed) return { placement: placed, bumped: true };
    }
    return { placement: null, bumped: false };
  }

  // Rounds are processed in order. A round cannot start before the week its previous round used
  // plus one, so a deferred game in an early round cascades all later rounds back.
  const MAX_WEEKS = 52;
  let lastUsedWeek = 0;
  for (const round of rounds) {
    const { nominalWeek, matches } = byRound.get(round);
    const earliest = Math.max(nominalWeek, lastUsedWeek + 1);
    for (const match of matches) {
      let placed = null;
      let bumped = false;
      let deferred = false;
      let placedWeek = null;
      for (let week = earliest; week < earliest + MAX_WEEKS; week++) {
        const attempt = placeAtWeek(match, week);
        if (attempt.placement) {
          placed = attempt.placement;
          bumped = attempt.bumped;
          deferred = week > match.nominalWeek;
          placedWeek = week;
          lastUsedWeek = Math.max(lastUsedWeek, week);
          break;
        }
      }
      result[match.slotId] = placed
        ? { ...placed, week: placedWeek, bumped, deferred }
        : { locationId: null, date: null, tableNumber: null, week: null, bumped: true, deferred: true };
    }
  }

  return result;
}